import { useAtom } from 'jotai'
import * as Sentry from '@sentry/react'
import { useMemo } from 'react'
import { editorAtom } from './constants'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { FEATURE_FLAGS } from '../../hooks/featureFlags'
import useHumanizerCredits from '../../hooks/useHumanizerCredits'
import { humanizedTextResultToDrafts } from './helpers'

export const useWordCount = (text: string) => {
	return useMemo(() => {
		const trimmedValue = text.trim().replace(/\s+/g, ' ')
		return trimmedValue.length === 0 ? 0 : trimmedValue.split(' ').length
	}, [text])
}

export const useHumanizeText = ({ onSuccess }: { onSuccess?: () => void }) => {
	const humanizerV2 = useFeatureFlagVariantKey(FEATURE_FLAGS.HUMANIZER_V4)
	const humanizeSentencesFF = useFeatureFlagVariantKey(FEATURE_FLAGS.SENTENCE_HUMANIZER)
	const maskHumanizerFF = useFeatureFlagVariantKey(FEATURE_FLAGS.MASK_HUMANIZER)
	const [editorState, setEditorState] = useAtom(editorAtom)
	const wordCount = useWordCount(editorState.textInputValue)
	const { consumeCredits } = useHumanizerCredits()

	async function makeHumanizerRequest(textToHumanize: string) {
		// Use new mask-humanize API when feature flag is enabled
		if (maskHumanizerFF === 'test') {
			return makeMaskHumanizerRequest(textToHumanize)
		}
		return makeLegacyHumanizerRequest(textToHumanize)
	}

	async function makeMaskHumanizerRequest(textToHumanize: string) {
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				text: textToHumanize,
			}),
		}
		try {
			// Submit the humanization job
			const submitResponse = await fetch(process.env.REACT_APP_API_ROOT + '/essay/mask-humanize/', requestOptions)
			const submitResult = await submitResponse.json()
			const jobId = submitResult.jobId

			if (!jobId) {
				throw new Error('No jobId returned from humanize submit')
			}

			// Poll for the result
			const pollForResult = async (): Promise<any> => {
				const pollResponse = await fetch(process.env.REACT_APP_API_ROOT + `/essay/mask-humanize/jobs/${jobId}/`, {
					method: 'GET',
					headers: { 'Content-Type': 'application/json' },
				})
				const pollResult = await pollResponse.json()

				if (pollResult.result !== undefined) {
					// Job is complete, return plain text with newlines preserved
					return { content: pollResult.result }
				}

				// Job not complete, wait and poll again
				await new Promise((resolve) => setTimeout(resolve, 1000))
				return pollForResult()
			}

			return await pollForResult()
		} catch (e) {
			Sentry.captureException(e)
			return ''
		}
	}

	async function makeLegacyHumanizerRequest(textToHumanize: string) {
		const requestOptions = {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				text: textToHumanize,
				humanizerIntensity: editorState.humanizerIntensityLevel,
				purpose: editorState.purpose,
				literacyLevel: editorState.literacyLevel,
				plainText: true,
				useHumanizerV4: humanizerV2 === 'test' ? true : false,
			}),
		}
		try {
			const response = await fetch(
				process.env.REACT_APP_API_ROOT +
					(humanizeSentencesFF === 'test' ? '/essay/humanize-sentence-free/' : '/essay/humanize/'),
				requestOptions
			)
			const result = await response.json()
			return result
		} catch (e) {
			Sentry.captureException(e)
			return ''
		}
	}

	const humanize = async (retry: boolean) => {
		setEditorState({
			...editorState,
			isLoadingHumanizeText: true,
			humanizedText: undefined,
		})

		let textToHumanize = editorState.previousHumanization
			? editorState.previousHumanization.text
			: editorState.textInputValue

		const humanizedTextResult = await makeHumanizerRequest(textToHumanize)
		setEditorState((editorState) => ({
			...editorState,
			isLoadingHumanizeText: false,
		}))

		// Mask humanizer always returns content directly
		if (maskHumanizerFF === 'test') {
			setEditorState((editorState) => ({
				...editorState,
				humanizedText: humanizedTextResult.content,
			}))
		} else if (humanizeSentencesFF === 'test') {
			let humanizedDrafts = humanizedTextResultToDrafts(humanizedTextResult)

			setEditorState((editorState) => ({
				...editorState,
				humanizedDrafts: humanizedDrafts,
			}))
		} else {
			setEditorState((editorState) => ({
				...editorState,
				humanizedText: humanizedTextResult.content,
			}))
		}
		onSuccess?.()

		if (!retry) {
			consumeCredits(wordCount)
		}

		setTimeout(
			() =>
				setEditorState((editorState) => ({
					...editorState,
					previousHumanization: {
						text: textToHumanize,
						intensityLevel: editorState.humanizerIntensityLevel,
					},
				})),
			500
		)
	}

	return humanize
}
